import { useState } from 'react'
import { Plus } from 'lucide-react'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { ManufacturerAssignmentPicker, type ManufacturerAssignment } from '@/components/manufacturer-assignment-picker'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { isWholeQuantity } from '../ui/numbers'
import { EditorDrawer } from './drawer'

type ManualLineType = 'custom' | 'service' | 'warranty'

const lineTypes: { value: ManualLineType; label: string }[] = [
  { value: 'custom', label: 'Custom item' },
  { value: 'service', label: 'Service' },
]

// A third-party warranty (e.g. Safeware) under equipment is ordered from its own provider (USA-1100).
const subLineTypes: { value: ManualLineType; label: string }[] = [
  ...lineTypes,
  { value: 'warranty', label: 'Warranty' },
]

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs font-medium text-muted-foreground" htmlFor={id}>
        {label}
      </Label>
      {children}
    </div>
  )
}

/** Add a custom item or service, at the top level or beneath a line (demo `CustomLineDrawer`). */
export function CustomLineDrawer({
  open,
  busy,
  parentName,
  onOpenChange,
  onSave,
}: {
  open: boolean
  busy: boolean
  parentName?: string | null
  onOpenChange: (open: boolean) => void
  onSave: (values: Record<string, unknown>) => void
}) {
  const [lineType, setLineType] = useState<ManualLineType>('custom')
  const [description, setDescription] = useState('')
  const [net, setNet] = useState('0')
  const [sell, setSell] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('1')
  const [manufacturer, setManufacturer] = useState<ManufacturerAssignment | null>(null)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (!open) {
      setLineType('custom')
      setDescription('')
      setNet('0')
      setSell(null)
      setQuantity('1')
      setManufacturer(null)
    }
  }
  // A top-level custom item or a warranty is ordered from the manufacturer chosen here (USA-1100).
  const ordered = (lineType === 'custom' && !parentName) || lineType === 'warranty'
  const quantityValid = isWholeQuantity(quantity)
  const valid = description.trim() && quantityValid && Number(net) >= 0

  return (
    <EditorDrawer
      onOpenChange={onOpenChange}
      open={open}
      title={parentName ? `Add sub-item to ${parentName}` : 'Add custom line'}
    >
      <form
        className="grid content-start gap-5 overflow-y-auto p-5"
        onSubmit={(event) => {
          event.preventDefault()
          if (!valid) return
          onSave({
            line_type: lineType === 'warranty' ? 'custom' : lineType,
            ...(lineType === 'warranty' ? { is_warranty: true } : {}),
            description: description.trim(),
            list_amount: net || '0',
            source_net_amount: net || '0',
            sell_amount: sell,
            quantity,
            ...(ordered && manufacturer ? { manufacturer_id: manufacturer.id } : {}),
          })
        }}
      >
        <div className="grid gap-1.5">
          <span aria-hidden="true" className="text-xs font-medium text-muted-foreground">
            Line type
          </span>
          <ReviewChoiceGroup
            label="Line type"
            onChange={setLineType}
            options={parentName ? subLineTypes : lineTypes}
            value={lineType}
          />
        </div>
        <Field id="custom-line-description" label="Description">
          <InlineFieldInput
            id="custom-line-description"
            onChange={(event) => setDescription(event.target.value)}
            required
            value={description}
          />
        </Field>
        {ordered ? (
          <div className="grid gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Manufacturer</span>
            <ManufacturerAssignmentPicker
              allowCreate
              className="w-fit border"
              currentId={manufacturer?.id}
              currentName={manufacturer?.name}
              onChange={setManufacturer}
            />
          </div>
        ) : null}
        <div className="grid grid-cols-3 gap-3">
          <Field id="custom-line-net" label="Net">
            <InlineFieldInput
              id="custom-line-net"
              inputMode="decimal"
              onChange={(event) => setNet(event.target.value)}
              value={net}
            />
          </Field>
          <Field id="custom-line-sell" label="Sell">
            <InlineFieldInput
              id="custom-line-sell"
              inputMode="decimal"
              onChange={(event) => setSell(event.target.value || null)}
              value={sell ?? (Number(net || 0) / 0.9).toFixed(2)}
            />
          </Field>
          <Field id="custom-line-quantity" label="Quantity">
            <InlineFieldInput
              aria-describedby={quantityValid ? undefined : 'custom-line-quantity-error'}
              aria-invalid={!quantityValid}
              id="custom-line-quantity"
              inputMode="numeric"
              onChange={(event) => setQuantity(event.target.value)}
              value={quantity}
            />
          </Field>
        </div>
        {quantityValid ? null : (
          <p className="-mt-2 text-sm text-destructive" id="custom-line-quantity-error" role="alert">
            Quantity must be a whole number of 1 or more.
          </p>
        )}
        <LoadingButton className="ml-auto" disabled={!valid} loading={busy} loadingText="Adding..." type="submit">
          <Plus />
          Add line
        </LoadingButton>
      </form>
    </EditorDrawer>
  )
}
